<script setup lang="ts">
import { ref, computed } from 'vue';
import { $t } from '@/composables/translations/useTranslate.ts';

interface Props {
  bookingNumber: string;
  shareUrl?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  shareUrl: null,
});

const copied = ref(false);

const facebookShareUrl = computed(() => {
  if (!props.shareUrl) return null;
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(props.shareUrl)}`;
});

const whatsappShareUrl = computed(() => {
  if (!props.shareUrl) return null;
  const text = $t('ibe.checkout.confirmation.mailbody', 'I found this great trip:');
  return `https://wa.me/?text=${encodeURIComponent(text + '\n' + props.shareUrl)}`;
});

const emailShareUrl = computed(() => {
  if (!props.shareUrl) return null;
  const subject = $t('ibe.checkout.confirmation.mailsubject', 'Check out this trip!');
  const body =
    $t('ibe.checkout.confirmation.mailbody', 'I found this great trip:') + '\n' + props.shareUrl;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
});

const copyLink = async () => {
  const urlToCopy = props.shareUrl || window.location.href;

  try {
    await navigator.clipboard.writeText(urlToCopy);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (error) {
    console.error('Failed to copy link:', error);
    copied.value = false;
  }
};

const printPage = () => {
  window.print();
};
</script>

<template>
  <div class="flex flex-col items-center justify-center w-full mt-8 mb-24">
    <p class="text-body mb-2.5">
      {{ $t('ibe.checkout.confirmation.title', 'Your booking number') }}
    </p>
    <h1 class="mb-5 font-bold text-text-primary">{{ props.bookingNumber }}</h1>
    <div class="flex flex-col md:flex-row w-full gap-5 md:gap-12 items-center justify-center">
      <button class="btn-tertiary h-[50px] w-full md:w-auto print:hidden" @click="printPage">
        {{ $t('ibe.checkout.confirmation.print', 'Print') }}
      </button>

      <div class="flex flex-col items-center md:items-start print:hidden">
        <p class="text-body mb-1.5">
          {{ $t('ibe.checkout.confirmation.share', 'Share this booking') }}
        </p>
        <div class="flex items-center gap-5">
          <!-- Facebook -->
          <a
            v-if="facebookShareUrl"
            :href="facebookShareUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="w-8 h-8 icon icon-facebook bg-highlight-1"></i>
          </a>
          <span v-else class="opacity-50 cursor-not-allowed">
            <i class="w-8 h-8 icon icon-facebook bg-highlight-1"></i>
          </span>

          <!-- WhatsApp -->
          <a
            v-if="whatsappShareUrl"
            :href="whatsappShareUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="w-8 h-8 icon icon-whatsapp bg-highlight-1"></i>
          </a>
          <span v-else class="opacity-50 cursor-not-allowed">
            <i class="w-8 h-8 icon icon-whatsapp bg-highlight-1"></i>
          </span>

          <!-- Email -->
          <a v-if="emailShareUrl" :href="emailShareUrl">
            <i class="w-8 h-8 icon icon-mail bg-highlight-1"></i>
          </a>
          <span v-else class="opacity-50 cursor-not-allowed">
            <i class="w-8 h-8 icon icon-mail bg-highlight-1"></i>
          </span>

          <!-- Copy Link - inline on desktop -->
          <button
            type="button"
            class="hidden md:flex gap-1.5 items-center text-text-primary text-action"
            @click="copyLink"
          >
            <i class="w-5 h-5 icon icon-clip bg-highlight-1"></i>
            {{
              copied
                ? $t('ibe.checkout.confirmation.copysuccess', 'Copied!')
                : $t('ibe.checkout.confirmation.copy', 'Copy link')
            }}
          </button>
        </div>

        <!-- Copy Link - below on mobile -->
        <button
          type="button"
          class="flex md:hidden gap-1.5 items-center text-text-primary text-action mt-4"
          @click="copyLink"
        >
          <i class="w-5 h-5 icon icon-clip bg-highlight-1"></i>
          {{
            copied
              ? $t('ibe.checkout.confirmation.copysuccess', 'Copied!')
              : $t('ibe.checkout.confirmation.copy', 'Copy link')
          }}
        </button>
      </div>
    </div>
  </div>
</template>
